import { useLayoutEffect } from "react";
import { SERVICEBOSS_FIELD_CSS } from "../lib/serviceBossSeo.js";
import AboutPage from "./pages/AboutPage";
import CheckoutPage from "./pages/CheckoutPage";
import ContactPage from "./pages/ContactPage";
import FreeCarCheckPage from "./pages/FreeCarCheckPage";
import GuideArticlePage from "./pages/GuideArticlePage";
import GuidesIndexPage from "./pages/GuidesIndexPage";
import HomePage from "./pages/HomePage";
import InsuranceGroupPage from "./pages/InsuranceGroupPage";
import NotFoundPage from "./pages/NotFoundPage";
import PricingPage from "./pages/PricingPage";
import PrivacyPage from "./pages/PrivacyPage";
import ReportPage from "./pages/ReportPage";
import TaxCalculatorPage from "./pages/TaxCalculatorPage";
import TermsPage from "./pages/TermsPage";
import UlezCheckerPage, { hasUlezChecker } from "./pages/UlezCheckerPage";
import V5cAlertsPage from "./pages/V5cAlertsPage";
import MakesPage from "./pages/MakesPage";
import CheapestHpiCheckPage from "./pages/CheapestHpiCheckPage";
import { hasCheapestHpiPage } from "./content/cheapestHpiCheck";
import { ServiceHistoryBrandPage, ServiceHistoryCheckPage } from "./pages/ServiceHistoryPages";
import { SERVICE_HISTORY_BRANDS, hasServiceHistoryPages, serviceHistoryBrandSlugFromPath } from "./content/serviceHistoryRoutes";
import AnalyticsConsent from "./components/AnalyticsConsent";
import { getBrand } from "./config/brands";
import { guideContent } from "./content/guides";
import { guideSlugFromPath } from "../lib/guides.js";
import "./brands/servicelad/cloud-white.css";

const routes = {
  "/": HomePage,
  "/index.html": HomePage,
  "/checkout": CheckoutPage,
  "/checkout.html": CheckoutPage,
  "/report": ReportPage,
  "/report.html": ReportPage,
  "/car-tax-calculator": TaxCalculatorPage,
  "/car-tax-calculator/": TaxCalculatorPage,
  "/car-tax-calculator.html": TaxCalculatorPage,
  "/tax-calculator": TaxCalculatorPage,
  "/v5c-alerts": V5cAlertsPage,
  "/v5c-alerts/": V5cAlertsPage,
  "/v5c-alerts.html": V5cAlertsPage,
  "/insurance-groups": InsuranceGroupPage,
  "/insurance-groups/": InsuranceGroupPage,
  "/about": AboutPage,
  "/about.html": AboutPage,
  "/contact": ContactPage,
  "/contact.html": ContactPage,
  "/terms": TermsPage,
  "/terms/": TermsPage,
  "/terms.html": TermsPage,
  "/privacy": PrivacyPage,
  "/privacy/": PrivacyPage,
  "/privacy.html": PrivacyPage,
  "/guides": GuidesIndexPage,
  "/guides/": GuidesIndexPage,
  "/free-car-check": FreeCarCheckPage,
  "/free-car-check/": FreeCarCheckPage,
  // ServiceByReg and ServiceLad only. Guarded below: brands without a ULEZ
  // checker get NotFound rather than a soft homepage.
  "/ulez-checker": UlezCheckerPage,
  "/ulez-checker/": UlezCheckerPage,
  "/ulez-checker.html": UlezCheckerPage,
  "/pricing": PricingPage,
  "/pricing/": PricingPage,
  "/pricing.html": PricingPage,
  "/makes": MakesPage,
  "/makes/": MakesPage,
  "/makes.html": MakesPage,
  // ServiceLad only, guarded below like the ULEZ checker.
  "/cheapest-hpi-check": CheapestHpiCheckPage,
  // ServiceBoss only, guarded below. Brand pages are matched by pattern in App().
  "/service-history-check": ServiceHistoryCheckPage,
  "/service-history-check/": ServiceHistoryCheckPage,
};

function makeParamsFromPath(pathname) {
  const match = pathname.match(/^\/makes(?:\/([a-z0-9-]+)(?:\/([a-z0-9-]+))?)?\/?$/);
  if (!match) return null;
  return {
    make: match[1] || null,
    model: match[2] || null,
  };
}

// iOS Safari auto-zooms the whole page when a tapped field's font-size is
// under 16px. The custom property lets deliberately larger fields keep
// their designed size while ordinary mobile fields retain zoom protection.
const NO_FOCUS_ZOOM_CSS = `
  @media (hover: none) and (pointer: coarse), (max-width: 900px) {
    input:not([type="checkbox"]):not([type="radio"]), select, textarea, [contenteditable="true"] {
      font-size: max(16px, var(--mobile-input-font-size, 16px)) !important;
    }
  }
`;

// This blocks pinch to zoom whatever the viewport says, so ServiceBoss, which
// allows zoom (lib/serviceBossSeo.js), only gets the field sizes above.
const NO_MOBILE_ZOOM_CSS = `
  html, body, a, button, input, select, textarea, label {
    touch-action: pan-x pan-y;
  }
${NO_FOCUS_ZOOM_CSS}`;

function zoomCss(brand) {
  return brand.key === "serviceboss" ? `${SERVICEBOSS_FIELD_CSS}${NO_FOCUS_ZOOM_CSS}` : NO_MOBILE_ZOOM_CSS;
}

export default function App() {
  // Show the page only once React has drawn it; the prerendered copy stays
  // hidden until now (see PRERENDER_HIDE_CSS in scripts/lib/prerender-shell.mjs).
  useLayoutEffect(() => {
    document.getElementById("root")?.classList.remove("prerendered");
  }, []);
  const brand = getBrand(window.location.hostname);
  // Apply the theme before rendering so the browser never paints the
  // default ServiceBoss accent before a configured brand is available.
  document.documentElement.dataset.brand = brand.key;

  if (brand.key === "servicelad") {
    ensureCloudWhiteFonts();
    document.documentElement.style.margin = "0";
    document.documentElement.style.padding = "0";
    document.body.style.margin = "0";
    document.body.style.padding = "0";
  }

  const guideSlug = guideSlugFromPath(window.location.pathname);
  if (guideSlug) {
    const data = guideContent[brand.key]?.[guideSlug];
    if (data) {
      return (
        <>
          <style>{zoomCss(brand)}</style>
          <GuideArticlePage brand={brand} data={data} />
          <AnalyticsConsent brandKey={brand.key} />
        </>
      );
    }
    return (
      <>
        <style>{zoomCss(brand)}</style>
        <NotFoundPage brand={brand} />
        <AnalyticsConsent brandKey={brand.key} />
      </>
    );
  }

  const makeParams = makeParamsFromPath(window.location.pathname);
  if (makeParams) {
    return (
      <>
        <style>{zoomCss(brand)}</style>
        <MakesPage brand={brand} makeSlug={makeParams.make} modelSlug={makeParams.model} />
        <AnalyticsConsent brandKey={brand.key} />
      </>
    );
  }

  const serviceHistorySlug = serviceHistoryBrandSlugFromPath(window.location.pathname);
  if (serviceHistorySlug) {
    const known = hasServiceHistoryPages(brand.key) && SERVICE_HISTORY_BRANDS.some((entry) => entry.slug === serviceHistorySlug);
    return (
      <>
        <style>{zoomCss(brand)}</style>
        {known ? <ServiceHistoryBrandPage brand={brand} slug={serviceHistorySlug} /> : <NotFoundPage brand={brand} />}
        <AnalyticsConsent brandKey={brand.key} />
      </>
    );
  }

  const matched = routes[window.location.pathname];
  let Page = matched || NotFoundPage;
  if (matched === ServiceHistoryCheckPage && !hasServiceHistoryPages(brand.key)) {
    Page = NotFoundPage;
  }
  if (matched === UlezCheckerPage && !hasUlezChecker(brand.key)) {
    Page = NotFoundPage;
  }
  if (matched === CheapestHpiCheckPage && !hasCheapestHpiPage(brand.key)) {
    Page = NotFoundPage;
  }

  return (
    <>
      <style>{zoomCss(brand)}</style>
      <Page brand={brand} />
      <AnalyticsConsent brandKey={brand.key} />
    </>
  );
}

function ensureCloudWhiteFonts() {
  if (document.getElementById("sl-cloud-fonts")) return;
  const link = document.createElement("link");
  link.id = "sl-cloud-fonts";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&family=Outfit:wght@500;600;700&display=swap";
  document.head.appendChild(link);
}
